import type { Lesson, Resource } from '../content/catalog'
import { COMPANY } from './company'
import { DEFAULT_LOCALE, LOCALE_HTML_LANG, LOCALE_OG } from './i18n/config'
import type { Locale } from './i18n/config'
import { alternatesFor, localizedPath } from './i18n/routes'
import type { RouteKey, RouteParams } from './i18n/routes'
import { noIndexTitle, routeSeo } from './i18n/route-meta'
import type { RouteSeo } from './i18n/route-meta'
import { PRO_GATED_SELECTOR } from './paywall'
import {
  PAID_PLAN_IDS,
  PLANS,
  PRICING_CURRENCY,
  extraSeatPricePerMonth,
  pricePerBillingPeriod,
  pricePerMonth,
} from './pricing'
import type { PaidPlanId } from './pricing'

export type HreflangAlternate = { hrefLang: string; href: string }

export type PageMeta = {
  title: string
  description: string
  canonical: string
  /** Open Graph locale token, e.g. `en_US` / `de_CH`. */
  locale: string
  ogImage: string
  /** Absolute URL of the Twitter/X card image. */
  twitterImage: string
  /** hreflang alternates (en, de, x-default) as absolute URLs. */
  alternates?: HreflangAlternate[]
}

export const SITE_NAME = 'Agentic School'
export const CONTACT_EMAIL = 'info@agenticschool.dev'

/**
 * Agency that designed and built this site. Single source of truth for the
 * one honest "Built by" credit rendered in the footer's legal region. The
 * localized prefix ("Built by" / "Erstellt von") lives in the i18n
 * dictionaries; only the brand name + link are fixed here. The link is a
 * genuine, followed backlink (no `nofollow`) and opens in a new tab.
 */
export const SITE_CREDIT = {
  name: 'Aether Digital',
  href: 'https://aetherdigital.ch',
} as const

/**
 * Brand theme color from the generated favicon/asset pack. Single source of
 * truth for the manifest + `<meta name="theme-color">` + tile color.
 */
export const BRAND_THEME_COLOR = '#1D1D1F'

/**
 * Site-wide default social share images from the asset pack. Routes that supply
 * their own content-specific OG card (e.g. `/og/home.png`) override `ogImage`;
 * everything else falls back to these brand cards. Both are 1200x630.
 */
export const DEFAULT_OG_IMAGE = '/og-image.png'
export const DEFAULT_TWITTER_IMAGE = '/twitter-card.png'
export const OG_IMAGE_WIDTH = '1200'
export const OG_IMAGE_HEIGHT = '630'
/** Legal provider for JSON-LD, derived from the shared company identity. */
export const PROVIDER = {
  name: COMPANY.legalName,
  street: COMPANY.street,
  postalCode: COMPANY.postalCode,
  city: COMPANY.city,
  country: COMPANY.country.de,
  countryCode: COMPANY.countryCode,
} as const

export function getSiteUrl(explicit?: string): string {
  return (
    explicit ||
    import.meta.env.SITE_URL ||
    import.meta.env.VITE_SITE_URL ||
    'https://agenticschool.dev'
  ).replace(/\/+$/u, '')
}

export function getCanonicalUrl(siteUrl: string, path: string): string {
  const safePath = path.startsWith('/') ? path : `/${path}`
  return `${siteUrl.replace(/\/+$/u, '')}${safePath}`.replace(/([^:]\/)\/+/gu, '$1')
}

export function buildPageMeta(input: {
  title: string
  description: string
  path: string
  siteUrl?: string
  ogImage?: string
  locale?: Locale
  /**
   * When provided, hreflang alternates for this route are computed from the
   * shared route map so every localized page advertises its siblings + an
   * x-default. Static pages pass only `routeKey`; dynamic pages also pass the
   * captured `routeParams`.
   */
  routeKey?: RouteKey
  routeParams?: RouteParams
}): PageMeta {
  const siteUrl = getSiteUrl(input.siteUrl)
  const locale = input.locale ?? DEFAULT_LOCALE

  const alternates = input.routeKey
    ? alternatesFor(input.routeKey, input.routeParams).map((alt) => ({
        hrefLang: alt.hrefLang,
        href: getCanonicalUrl(siteUrl, alt.path),
      }))
    : undefined

  // Routes that supply a content-specific OG card use it for both og + twitter;
  // routes with no override fall back to the brand pack's dedicated cards.
  const ogImage = input.ogImage ?? DEFAULT_OG_IMAGE
  const twitterImage = input.ogImage ?? DEFAULT_TWITTER_IMAGE

  return {
    title: truncate(input.title, 70),
    description: truncate(input.description, 160),
    canonical: getCanonicalUrl(siteUrl, input.path),
    locale: LOCALE_OG[locale],
    ogImage: getCanonicalUrl(siteUrl, ogImage),
    twitterImage: getCanonicalUrl(siteUrl, twitterImage),
    alternates,
  }
}

export function toHeadTags(meta: PageMeta) {
  return {
    meta: [
      { title: meta.title },
      { name: 'description', content: meta.description },
      { property: 'og:title', content: meta.title },
      { property: 'og:description', content: meta.description },
      { property: 'og:url', content: meta.canonical },
      { property: 'og:image', content: meta.ogImage },
      { property: 'og:image:width', content: OG_IMAGE_WIDTH },
      { property: 'og:image:height', content: OG_IMAGE_HEIGHT },
      { property: 'og:image:alt', content: `${meta.title} - ${SITE_NAME}` },
      { property: 'og:site_name', content: SITE_NAME },
      { property: 'og:locale', content: meta.locale },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: meta.title },
      { name: 'twitter:description', content: meta.description },
      { name: 'twitter:image', content: meta.twitterImage },
    ],
    links: [
      { rel: 'canonical', href: meta.canonical },
      ...(meta.alternates ?? []).map((alt) => ({
        rel: 'alternate',
        hrefLang: alt.hrefLang,
        href: alt.href,
      })),
    ],
  }
}

/**
 * One-call head builder for a localized route: title, description, canonical,
 * hreflang alternates and og:locale, all derived from the shared route map.
 * Thin route wrappers use this so every page automatically carries correct
 * i18n SEO without hand-wiring alternates per route.
 */
export function buildLocalizedHead(input: {
  locale: Locale
  routeKey: RouteKey
  routeParams?: RouteParams
  path: string
  title: string
  description: string
  ogImage?: string
  siteUrl?: string
}) {
  return toHeadTags(
    buildPageMeta({
      title: input.title,
      description: input.description,
      path: input.path,
      ogImage: input.ogImage,
      siteUrl: input.siteUrl,
      locale: input.locale,
      routeKey: input.routeKey,
      routeParams: input.routeParams,
    }),
  )
}

/** `<html lang>` value for a locale (e.g. `en`, `de-CH`). */
export function htmlLang(locale: Locale): string {
  return LOCALE_HTML_LANG[locale]
}

/**
 * Head builder for a STATIC localized route. Pulls the per-locale SEO copy from
 * the shared {@link routeSeo} table and the path from the route map, so a route
 * file collapses to `head: () => staticRouteHead('courses', 'en')`.
 */
export function staticRouteHead(
  key: Parameters<typeof routeSeo>[0],
  locale: Locale,
) {
  const seo = routeSeo(key, locale)
  return buildLocalizedHead({
    locale,
    routeKey: key,
    path: localizedPath(key, locale),
    title: seo.title,
    description: seo.description,
    ogImage: seo.ogImage,
  })
}

/**
 * Head builder for a DYNAMIC localized route. The caller supplies the
 * locale-resolved title/description (from the localized catalog) and the
 * captured params; alternates + canonical are derived from the route map.
 */
export function dynamicRouteHead(input: {
  locale: Locale
  routeKey: RouteKey
  params: RouteParams
  title: string
  description: string
  ogImage?: string
}) {
  return buildLocalizedHead({
    locale: input.locale,
    routeKey: input.routeKey,
    routeParams: input.params,
    path: localizedPath(input.routeKey, input.locale, input.params),
    title: input.title,
    description: input.description,
    ogImage: input.ogImage,
  })
}

export type { RouteSeo }

/**
 * Head builder for a noindex (private/auth/account/admin) localized route. The
 * canonical points at the locale-correct path; the page is excluded from
 * indexing and from hreflang (a private route has no public alternates). Title
 * comes from the shared per-locale noindex table.
 */
export function noIndexRouteHead(
  key: Parameters<typeof noIndexTitle>[0],
  locale: Locale,
  params?: RouteParams,
) {
  return buildNoIndexMeta({
    title: noIndexTitle(key, locale),
    path: localizedPath(key, locale, params),
  })
}

const BRAND_SUFFIX = ' | Agentic School'

/**
 * Append the brand suffix only when the resulting title still fits the 60-char
 * SERP budget. Long entity titles are shown bare and rely on og:site_name.
 */
export function withBrand(title: string): string {
  return title.length + BRAND_SUFFIX.length <= 60 ? title + BRAND_SUFFIX : title
}

/**
 * Trim a description to <=158 chars on a word boundary so buildPageMeta's
 * 160-char hard truncation never appends an ellipsis.
 */
export function clampDescription(text: string, max = 158): string {
  if (text.length <= max) return text
  const cut = text.slice(0, max)
  const lastSpace = cut.lastIndexOf(' ')
  const trimmed = lastSpace > max - 24 ? cut.slice(0, lastSpace) : cut
  return trimmed.replace(/[\s,;:–-]+$/u, '').trim()
}

/**
 * Head tags for pages that should not be indexed (auth, account, admin,
 * onboarding and other private/utility surfaces). Still sets a clean title
 * and a self-referencing canonical, but tells crawlers to skip indexing and
 * link-following so thin/private routes never surface in search.
 */
export function buildNoIndexMeta(input: { title: string; path?: string }) {
  const tags: {
    meta: Array<Record<string, string>>
    links?: Array<Record<string, string>>
  } = {
    meta: [
      { title: truncate(input.title, 70) },
      { name: 'robots', content: 'noindex, nofollow' },
      { name: 'googlebot', content: 'noindex, nofollow' },
    ],
  }
  if (input.path) {
    tags.links = [
      { rel: 'canonical', href: getCanonicalUrl(getSiteUrl(), input.path) },
    ]
  }
  return tags
}

/**
 * Paywalled-content markup (Google "subscription and paywalled content"
 * guidance): a Pro lesson declares itself not free and points `hasPart` at the
 * gated container, so the free preview stays indexable without being treated
 * as cloaking. Free lessons are explicitly free.
 */
function lessonAccessJsonLd(lesson: Pick<Lesson, 'access'>) {
  if (lesson.access !== 'pro') return { isAccessibleForFree: true } as const
  return {
    isAccessibleForFree: false,
    hasPart: {
      '@type': 'WebPageElement',
      isAccessibleForFree: false,
      cssSelector: PRO_GATED_SELECTOR,
    },
  } as const
}

/**
 * Course `offers` derived from the pricing config (single source of truth).
 * Free courses advertise a free offer; Pro courses the Pro subscription, once
 * per billing interval with an explicit unit price.
 */
export function buildCourseOffersJsonLd(input: {
  tier: 'free' | 'pro'
  pricingUrl: string
}) {
  if (input.tier === 'free') {
    return [
      {
        '@type': 'Offer',
        category: 'Free',
        price: 0,
        priceCurrency: PRICING_CURRENCY,
        availability: 'https://schema.org/InStock',
        url: input.pricingUrl,
      },
    ]
  }
  return (
    [
      ['year', 'ANN'],
      ['month', 'MON'],
    ] as const
  ).map(([interval, unitCode]) => ({
    '@type': 'Offer',
    category: 'Subscription',
    name: `Agentic School Pro (${interval === 'year' ? 'yearly' : 'monthly'})`,
    price: pricePerBillingPeriod('pro', interval),
    priceCurrency: PRICING_CURRENCY,
    availability: 'https://schema.org/InStock',
    url: input.pricingUrl,
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: pricePerBillingPeriod('pro', interval),
      priceCurrency: PRICING_CURRENCY,
      unitCode,
      referenceQuantity: {
        '@type': 'QuantitativeValue',
        value: 1,
        unitCode,
      },
    },
    description: `${pricePerMonth('pro', interval)} ${PRICING_CURRENCY} per month, billed ${interval === 'year' ? 'yearly' : 'monthly'}`,
  }))
}

/** Product names for structured data (brand names, never translated). */
const PLAN_PRODUCT_NAMES: Record<PaidPlanId, string> = {
  pro: 'Agentic School Pro',
  team: 'Agentic School Team',
}

/**
 * Pricing page structured data: one `Product` per paid plan with an `Offer`
 * per billing interval (unit price per period), all derived from the pricing
 * config so the price in a rich result can never drift from the page.
 */
export function buildPricingJsonLd(input: {
  locale: Locale
  descriptions: Record<PaidPlanId, string>
  siteUrl?: string
}) {
  const siteUrl = getSiteUrl(input.siteUrl)
  const url = getCanonicalUrl(siteUrl, localizedPath('pricing', input.locale))
  return {
    '@context': 'https://schema.org',
    '@graph': PAID_PLAN_IDS.map((planId) => ({
      '@type': 'Product',
      '@id': `${url}#${planId}`,
      name: PLAN_PRODUCT_NAMES[planId],
      description: input.descriptions[planId],
      brand: { '@type': 'Brand', name: SITE_NAME },
      url,
      image: getCanonicalUrl(siteUrl, '/og/home.png'),
      offers: (
        [
          ['year', 'ANN'],
          ['month', 'MON'],
        ] as const
      ).map(([interval, unitCode]) => {
        const label = interval === 'year' ? 'yearly' : 'monthly'
        const extra =
          planId === 'team'
            ? `; extra seats ${extraSeatPricePerMonth()} ${PRICING_CURRENCY} per seat per month`
            : ''
        return {
          '@type': 'Offer',
          name: `${PLAN_PRODUCT_NAMES[planId]} (${label})`,
          price: pricePerBillingPeriod(planId, interval),
          priceCurrency: PRICING_CURRENCY,
          availability: 'https://schema.org/InStock',
          url,
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: pricePerBillingPeriod(planId, interval),
            priceCurrency: PRICING_CURRENCY,
            unitCode,
            ...(planId === 'team'
              ? {
                  eligibleQuantity: {
                    '@type': 'QuantitativeValue',
                    value: PLANS.team.seatsIncluded,
                    unitText: 'seats',
                  },
                }
              : {}),
          },
          description: `${pricePerMonth(planId, interval)} ${PRICING_CURRENCY} per month, billed ${label}${extra}. 30-day money-back guarantee.`,
        }
      }),
    })),
  }
}

export function buildLessonJsonLd(input: {
  lesson: Lesson
  courseTitle: string
  siteUrl?: string
  locale?: Locale
}) {
  const siteUrl = getSiteUrl(input.siteUrl)
  const locale = input.locale ?? DEFAULT_LOCALE
  const lessonUrl = getCanonicalUrl(
    siteUrl,
    localizedPath('lesson', locale, {
      courseSlug: input.lesson.courseSlug,
      lessonSlug: input.lesson.slug,
    }),
  )
  const inLanguage = LOCALE_HTML_LANG[locale]

  // Lessons are text-first. Only emit VideoObject structured data when a lesson
  // actually has a video; otherwise describe it as a LearningResource so the
  // page never advertises a video that does not exist.
  if (input.lesson.youtubeId) {
    return {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: input.lesson.title,
      description: input.lesson.summary,
      thumbnailUrl: getCanonicalUrl(siteUrl, input.lesson.thumbnailUrl),
      uploadDate: input.lesson.publishedAt,
      duration: input.lesson.durationIso,
      embedUrl: `https://www.youtube-nocookie.com/embed/${input.lesson.youtubeId}`,
      inLanguage,
      url: lessonUrl,
      isPartOf: {
        '@type': 'Course',
        name: input.courseTitle,
      },
      ...lessonAccessJsonLd(input.lesson),
    } as const
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'LearningResource',
    name: input.lesson.title,
    description: input.lesson.summary,
    learningResourceType: 'Lesson',
    educationalLevel: input.lesson.level,
    timeRequired: input.lesson.durationIso,
    inLanguage,
    url: lessonUrl,
    isPartOf: {
      '@type': 'Course',
      name: input.courseTitle,
    },
    ...lessonAccessJsonLd(input.lesson),
  } as const
}

export function buildOrganizationJsonLd(siteUrl?: string) {
  const base = getSiteUrl(siteUrl)

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    alternateName: 'Agentic Engineering Campus',
    url: base,
    logo: getCanonicalUrl(base, '/icon-1024x1024.png'),
    email: CONTACT_EMAIL,
    founder: { '@type': 'Person', name: PROVIDER.name },
    address: {
      '@type': 'PostalAddress',
      streetAddress: PROVIDER.street,
      postalCode: PROVIDER.postalCode,
      addressLocality: PROVIDER.city,
      addressCountry: PROVIDER.countryCode,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: CONTACT_EMAIL,
      contactType: 'customer support',
      availableLanguage: ['en', 'de', 'fr', 'it'],
    },
    sameAs: ['https://github.com/nicojaroszewski'],
  }
}

export function buildWebSiteJsonLd(siteUrl?: string, locale: Locale = DEFAULT_LOCALE) {
  const base = getSiteUrl(siteUrl)

  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: base,
    inLanguage: LOCALE_HTML_LANG[locale],
    publisher: { '@type': 'Organization', name: SITE_NAME, url: base },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${base}/suche?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  }
}

/**
 * Default publish/modify date for evergreen catalog content that does not
 * carry an explicit date (e.g. courses). Matches the seed catalog date.
 */
const DEFAULT_CONTENT_DATE = '2026-05-26'

type AuthorJsonLd = {
  '@type': 'Person'
  name: string
  url: string
}

type PublisherJsonLd = {
  '@type': 'Organization'
  name: string
  url: string
  logo: {
    '@type': 'ImageObject'
    url: string
  }
}

function buildAuthorJsonLd(siteUrl: string): AuthorJsonLd {
  return {
    '@type': 'Person',
    name: PROVIDER.name,
    url: getCanonicalUrl(siteUrl, '/impressum'),
  }
}

function buildPublisherJsonLd(siteUrl: string): PublisherJsonLd {
  return {
    '@type': 'Organization',
    name: SITE_NAME,
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: getCanonicalUrl(siteUrl, '/icon-1024x1024.png'),
    },
  }
}

export function buildCourseJsonLd(input: {
  title: string
  description: string
  path: string
  lessons: Lesson[]
  /** Access tier; drives `isAccessibleForFree` + `offers`. Defaults to free. */
  tier?: 'free' | 'pro'
  datePublished?: string
  dateModified?: string
  siteUrl?: string
  locale?: Locale
}) {
  const siteUrl = getSiteUrl(input.siteUrl)
  const locale = input.locale ?? DEFAULT_LOCALE
  const inLanguage = LOCALE_HTML_LANG[locale]
  const tier = input.tier ?? 'free'

  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: input.title,
    description: input.description,
    url: getCanonicalUrl(siteUrl, input.path),
    inLanguage,
    datePublished: input.datePublished ?? DEFAULT_CONTENT_DATE,
    dateModified: input.dateModified ?? DEFAULT_CONTENT_DATE,
    author: buildAuthorJsonLd(siteUrl),
    publisher: buildPublisherJsonLd(siteUrl),
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: siteUrl,
    },
    isAccessibleForFree: tier === 'free',
    offers: buildCourseOffersJsonLd({
      tier,
      pricingUrl: getCanonicalUrl(siteUrl, localizedPath('pricing', locale)),
    }),
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
      inLanguage,
      courseWorkload: `PT${input.lessons.length * 20}M`,
    },
    hasPart: buildItemListJsonLd(
      input.lessons.map((lesson) => ({
        name: lesson.title,
        path: localizedPath('lesson', locale, {
          courseSlug: lesson.courseSlug,
          lessonSlug: lesson.slug,
        }),
      })),
      siteUrl,
    ),
  }
}

export function buildItemListJsonLd(
  items: Array<{ name: string; path: string }>,
  siteUrl?: string,
) {
  const base = getSiteUrl(siteUrl)

  return {
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      url: getCanonicalUrl(base, item.path),
    })),
  }
}

export function buildBreadcrumbJsonLd(
  crumbs: Array<{ name: string; path: string }>,
  siteUrl?: string,
) {
  const base = getSiteUrl(siteUrl)

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: getCanonicalUrl(base, crumb.path),
    })),
  }
}

export function buildResourceJsonLd(input: {
  resource: Resource
  path: string
  siteUrl?: string
  locale?: Locale
}) {
  const siteUrl = getSiteUrl(input.siteUrl)
  const pageUrl = getCanonicalUrl(siteUrl, input.path)
  const locale = input.locale ?? DEFAULT_LOCALE

  return {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: input.resource.title,
    description: input.resource.shortDescription,
    articleBody: input.resource.body,
    inLanguage: LOCALE_HTML_LANG[locale],
    url: pageUrl,
    mainEntityOfPage: pageUrl,
    datePublished: input.resource.lastReviewedAt,
    dateModified: input.resource.lastReviewedAt,
    keywords: input.resource.tags.join(', '),
    about: input.resource.tags,
    author: buildAuthorJsonLd(siteUrl),
    publisher: buildPublisherJsonLd(siteUrl),
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', '.resource-intro'],
    },
  }
}

export function buildBlogPostingJsonLd(input: {
  title: string
  description: string
  body: string
  datePublished: string
  path: string
  keywords?: string
  siteUrl?: string
  locale?: Locale
}) {
  const siteUrl = getSiteUrl(input.siteUrl)
  const pageUrl = getCanonicalUrl(siteUrl, input.path)
  const locale = input.locale ?? DEFAULT_LOCALE

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: input.title,
    description: input.description,
    articleBody: input.body,
    inLanguage: LOCALE_HTML_LANG[locale],
    url: pageUrl,
    mainEntityOfPage: pageUrl,
    datePublished: input.datePublished,
    dateModified: input.datePublished,
    keywords: input.keywords,
    author: buildAuthorJsonLd(siteUrl),
    publisher: buildPublisherJsonLd(siteUrl),
  }
}

export function buildHowToJsonLd(input: {
  name: string
  description: string
  steps: Array<{ name: string; text: string }>
  path: string
  siteUrl?: string
  locale?: Locale
}) {
  const siteUrl = getSiteUrl(input.siteUrl)
  const pageUrl = getCanonicalUrl(siteUrl, input.path)
  const locale = input.locale ?? DEFAULT_LOCALE

  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: input.name,
    description: input.description,
    inLanguage: LOCALE_HTML_LANG[locale],
    url: pageUrl,
    mainEntityOfPage: pageUrl,
    author: buildAuthorJsonLd(siteUrl),
    publisher: buildPublisherJsonLd(siteUrl),
    step: input.steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
  }
}

export function buildFaqJsonLd(
  items: ReadonlyArray<{ question: string; answer: string }>,
  locale: Locale = DEFAULT_LOCALE,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: LOCALE_HTML_LANG[locale],
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }
}

/**
 * DefinedTerm structured data for a glossary entry. Helps answer engines treat
 * the page as an authoritative definition of the term. Paired with the
 * BlogPosting/Article + FAQPage emitted by the page so the entry is both an
 * article and a defined term.
 */
export function buildDefinedTermJsonLd(input: {
  name: string
  description: string
  path: string
  siteUrl?: string
  locale?: Locale
}) {
  const siteUrl = getSiteUrl(input.siteUrl)
  const locale = input.locale ?? DEFAULT_LOCALE
  const pageUrl = getCanonicalUrl(siteUrl, input.path)

  return {
    '@context': 'https://schema.org',
    '@type': 'DefinedTerm',
    name: input.name,
    description: input.description,
    inLanguage: LOCALE_HTML_LANG[locale],
    url: pageUrl,
    inDefinedTermSet: {
      '@type': 'DefinedTermSet',
      name: `${SITE_NAME} Glossary`,
      url: pageUrl,
    },
  }
}

/**
 * SoftwareApplication / WebApplication structured data for a free interactive
 * tool. The tool runs fully client-side at no cost, so it is advertised as a
 * free WebApplication with a zero-price offer. Paired with a FAQPage +
 * BreadcrumbList emitted by the tool page.
 */
export function buildSoftwareAppJsonLd(input: {
  name: string
  description: string
  path: string
  category?: string
  siteUrl?: string
  locale?: Locale
}) {
  const siteUrl = getSiteUrl(input.siteUrl)
  const locale = input.locale ?? DEFAULT_LOCALE
  const pageUrl = getCanonicalUrl(siteUrl, input.path)

  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: input.name,
    description: input.description,
    url: pageUrl,
    inLanguage: LOCALE_HTML_LANG[locale],
    applicationCategory: input.category ?? 'DeveloperApplication',
    operatingSystem: 'Any (web browser)',
    browserRequirements: 'Requires JavaScript and a modern web browser.',
    isAccessibleForFree: true,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    publisher: buildPublisherJsonLd(siteUrl),
  }
}

export function buildPersonJsonLd(siteUrl?: string) {
  const base = getSiteUrl(siteUrl)

  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: PROVIDER.name,
    url: getCanonicalUrl(base, '/impressum'),
    jobTitle: 'Founder',
    worksFor: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: base,
    },
    sameAs: ['https://github.com/nicojaroszewski'],
  }
}

function truncate(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value
  return `${value.slice(0, maxLength - 1).trim()}...`
}
